/// Toolbar: File menu, edit controls, zoom, tool selector, and run/debug controls.
/// Six sections separated by dividers, fully wired to editorStore and engineStore.

import { useCallback, useEffect } from "react";
import {
  FileIcon,
  FilePlus2,
  FolderOpen,
  Save,
  SaveAll,
  Image,
  FileCode,
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  MousePointer2,
  Hand,
  Spline,
  Rocket,
  Play,
  Square,
  Columns2,
  Rows2,
  LayoutDashboard,
  Loader2,
  Workflow,
  ChevronDown,
} from "lucide-react";
import { IconButton } from "@/components/common/IconButton";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/common/DropdownMenu";
import { DebugControls } from "@/components/integration/DebugControls";
import { useToolbarActions } from "@/hooks/useToolbarActions";
import { useEditorStore } from "@/stores/editorStore";
import { useUIStore, type LayoutMode } from "@/stores/uiStore";
import type { EditorType, ToolMode } from "@/types/editor";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let tabCounter = 0;

function createNewTab(editorType: EditorType) {
  const store = useEditorStore.getState();
  tabCounter += 1;
  const label = editorType === "bpmn" ? "BPMN" : "Node";
  store.addTab({
    id: `tab-${Date.now()}-${tabCounter}`,
    title: `Untitled ${label} ${tabCounter}`,
    editorType,
    content: "",
    dirty: false,
    canUndo: false,
    canRedo: false,
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Toolbar() {
  const activeTab = useEditorStore((s) => {
    const id = s.activeTabId;
    return id ? s.tabs.find((t) => t.id === id) : undefined;
  });
  const activeEditorState = useEditorStore((s) => {
    const id = s.activeTabId;
    return id ? s.editorStates.get(id) : undefined;
  });
  const setToolMode = useEditorStore((s) => s.setToolMode);

  const { deploy, run, stop, canDeploy, canRun, canStop, isDeploying, isStopping } = useToolbarActions();

  const layoutMode = useUIStore((s) => s.layoutMode);
  const setLayoutMode = useUIStore((s) => s.setLayoutMode);

  const canUndo = activeTab?.canUndo ?? false;
  const canRedo = activeTab?.canRedo ?? false;
  const currentTool: ToolMode = activeEditorState?.toolMode ?? "select";
  const currentZoom = activeEditorState?.viewport.zoom ?? 1.0;
  const zoomPercent = Math.round(currentZoom * 100);

  // Tool selection handler
  const handleToolChange = useCallback(
    (mode: ToolMode) => {
      const state = useEditorStore.getState();
      if (state.activeTabId) {
        setToolMode(state.activeTabId, mode);
      }
    },
    [setToolMode],
  );

  // Edit command dispatch (via worker)
  const sendEditCommand = useCallback((action: string) => {
    const event = new CustomEvent("flowy:editCommand", { detail: { action } });
    window.dispatchEvent(event);
  }, []);

  // Save document (trigger export from WASM -> download)
  const handleSave = useCallback(() => {
    window.dispatchEvent(new CustomEvent("flowy:saveDocument"));
  }, []);

  // Open file dialog: reads .bpmn/.xml/.json files and dispatches load/import events
  const handleOpenFile = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".bpmn,.xml,.json";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const content = await file.text();
      const isBpmn = file.name.endsWith(".bpmn") || file.name.endsWith(".xml");
      const isN8N = file.name.includes(".n8n.json") || file.name.includes("n8n");
      if (isN8N) {
        window.dispatchEvent(new CustomEvent("flowy:importN8N", { detail: { content } }));
      } else {
        window.dispatchEvent(
          new CustomEvent("flowy:loadDocument", {
            detail: { content, format: isBpmn ? "bpmn" : "nodeflow" },
          }),
        );
      }
    };
    input.click();
  }, []);

  // Zoom command dispatch
  const sendZoomCommand = useCallback((delta: number) => {
    const event = new CustomEvent("flowy:zoomCommand", { detail: { delta } });
    window.dispatchEvent(event);
  }, []);

  const sendFitToView = useCallback(() => {
    const event = new CustomEvent("flowy:zoomCommand", { detail: { fit: true } });
    window.dispatchEvent(event);
  }, []);

  const sendResetZoom = useCallback(() => {
    const event = new CustomEvent("flowy:zoomCommand", { detail: { reset: true } });
    window.dispatchEvent(event);
  }, []);

  // Auto-layout dispatch: selection-aware, algorithm-parameterized
  const dispatchAutoLayout = useCallback((algorithm: number) => {
    const state = useEditorStore.getState();
    const tabId = state.activeTabId;
    const editorState = tabId ? state.editorStates.get(tabId) : undefined;
    const hasSelection = (editorState?.selection.selectedIds.length ?? 0) > 0;
    window.dispatchEvent(
      new CustomEvent("flowy:autoLayout", {
        detail: { selectedOnly: hasSelection, algorithm },
      }),
    );
  }, []);

  // Layout toggle
  const cycleLayout = useCallback(
    (mode: LayoutMode) => {
      setLayoutMode(layoutMode === mode ? "single" : mode);
    },
    [layoutMode, setLayoutMode],
  );

  // -------------------------------------------------------------------------
  // Global keyboard shortcuts
  // -------------------------------------------------------------------------

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const ctrl = e.ctrlKey || e.metaKey;

      // Check if focus is in a text input -- let the browser handle native
      // text editing shortcuts (Ctrl+Z/C/X/V/A, Delete, Backspace) natively.
      const target = e.target as HTMLElement;
      const isTextInput =
        target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;

      // File shortcuts (always active, even in text inputs)
      if (ctrl && !e.shiftKey && e.key === "n") {
        e.preventDefault();
        createNewTab("bpmn");
        return;
      }
      if (ctrl && e.shiftKey && e.key === "N") {
        e.preventDefault();
        createNewTab("nodeflow");
        return;
      }
      if (ctrl && e.key === "s") {
        e.preventDefault();
        handleSave();
        return;
      }
      if (ctrl && e.key === "o") {
        e.preventDefault();
        handleOpenFile();
        return;
      }

      // Tool mode shortcuts (V/H/C/Escape) -- standalone keys, no modifier.
      // Placed before Ctrl-check block so they fire on bare key press only.
      if (!isTextInput && !ctrl && !e.altKey) {
        if (e.key === "v" || e.key === "V") {
          e.preventDefault();
          handleToolChange("select");
          return;
        }
        if (e.key === "h" || e.key === "H") {
          e.preventDefault();
          handleToolChange("pan");
          return;
        }
        if (e.key === "c" || e.key === "C") {
          e.preventDefault();
          handleToolChange("addConnection");
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          // If in connection mode, exit to select and cancel any active drag
          const state = useEditorStore.getState();
          const tabId = state.activeTabId;
          const editorState = tabId ? state.editorStates.get(tabId) : undefined;
          if (editorState?.toolMode === "addConnection") {
            handleToolChange("select");
            // Dispatch cancelConnectDrag to worker to abort any active drag preview
            window.dispatchEvent(new CustomEvent("flowy:cancelConnectDrag"));
          }
          return;
        }
      }

      // Edit shortcuts -- skip when focus is in a text input so native
      // undo/copy/cut/paste/select-all work correctly in property fields.
      if (!isTextInput) {
        if (ctrl && e.key === "z" && !e.shiftKey) {
          e.preventDefault();
          sendEditCommand("undo");
          return;
        }
        if (ctrl && (e.key === "y" || (e.shiftKey && e.key === "Z"))) {
          e.preventDefault();
          sendEditCommand("redo");
          return;
        }
        if (ctrl && e.key === "c") {
          e.preventDefault();
          sendEditCommand("copy");
          return;
        }
        if (ctrl && e.key === "x") {
          e.preventDefault();
          sendEditCommand("cut");
          return;
        }
        if (ctrl && e.key === "v") {
          e.preventDefault();
          sendEditCommand("paste");
          return;
        }
        if (ctrl && e.key === "a") {
          e.preventDefault();
          sendEditCommand("selectAll");
          return;
        }
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        // Only handle if not in an input element
        if (!isTextInput) {
          e.preventDefault();
          sendEditCommand("delete");
        }
        return;
      }

      // Zoom shortcuts
      if (ctrl && e.key === "=") {
        e.preventDefault();
        sendZoomCommand(0.25);
        return;
      }
      if (ctrl && e.key === "-") {
        e.preventDefault();
        sendZoomCommand(-0.25);
        return;
      }
      if (ctrl && e.key === "0") {
        e.preventDefault();
        sendFitToView();
        return;
      }
      if (ctrl && e.key === "1") {
        e.preventDefault();
        sendResetZoom();
        return;
      }

      // Auto-layout shortcut
      if (ctrl && e.key === "l") {
        e.preventDefault();
        // Default algorithm: 0 (Sugiyama for BPMN, DAG for Node)
        dispatchAutoLayout(0);
        return;
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    sendEditCommand,
    sendZoomCommand,
    sendFitToView,
    sendResetZoom,
    handleSave,
    handleOpenFile,
    handleToolChange,
    dispatchAutoLayout,
  ]);

  return (
    <div data-testid="toolbar" className="flex h-10 items-center border-b border-zinc-700 bg-zinc-800 px-1 gap-0.5 select-none">
      {/* Section 1: File Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1 rounded px-2 py-1 text-sm text-zinc-300 hover:bg-zinc-700 hover:text-zinc-100 transition-colors"
          >
            <FileIcon className="h-4 w-4" />
            <span>File</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem shortcut="Ctrl+N" onSelect={() => createNewTab("bpmn")}>
            <FilePlus2 className="h-4 w-4 text-zinc-400" />
            New BPMN Diagram
          </DropdownMenuItem>
          <DropdownMenuItem shortcut="Ctrl+Shift+N" onSelect={() => createNewTab("nodeflow")}>
            <FilePlus2 className="h-4 w-4 text-zinc-400" />
            New Node Flow
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem shortcut="Ctrl+O" onSelect={handleOpenFile}>
            <FolderOpen className="h-4 w-4 text-zinc-400" />
            Open File...
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem shortcut="Ctrl+S" onSelect={handleSave}>
            <Save className="h-4 w-4 text-zinc-400" />
            Save
          </DropdownMenuItem>
          <DropdownMenuItem shortcut="Ctrl+Shift+S" onSelect={handleSave}>
            <SaveAll className="h-4 w-4 text-zinc-400" />
            Save As...
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem>
            <Image className="h-4 w-4 text-zinc-400" />
            Export as PNG
          </DropdownMenuItem>
          <DropdownMenuItem>
            <FileCode className="h-4 w-4 text-zinc-400" />
            Export as SVG
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Divider */}
      <div className="mx-1 h-5 w-px bg-zinc-700" />

      {/* Section 2: Edit (Undo/Redo) */}
      <IconButton
        icon={<Undo2 className="h-4 w-4" />}
        label="Undo (Ctrl+Z)"
        disabled={!canUndo}
        onClick={() => sendEditCommand("undo")}
      />
      <IconButton
        icon={<Redo2 className="h-4 w-4" />}
        label="Redo (Ctrl+Y)"
        disabled={!canRedo}
        onClick={() => sendEditCommand("redo")}
      />

      {/* Divider */}
      <div className="mx-1 h-5 w-px bg-zinc-700" />

      {/* Section 3: Zoom Controls */}
      <IconButton
        icon={<ZoomOut className="h-4 w-4" />}
        label="Zoom Out (-)"
        onClick={() => sendZoomCommand(-0.25)}
      />
      <button
        type="button"
        className="min-w-[48px] rounded px-1.5 py-0.5 text-xs text-zinc-300 hover:bg-zinc-700 transition-colors tabular-nums text-center"
        onClick={sendResetZoom}
        title="Reset to 100% (Ctrl+1)"
      >
        {zoomPercent}%
      </button>
      <IconButton
        icon={<ZoomIn className="h-4 w-4" />}
        label="Zoom In (+)"
        onClick={() => sendZoomCommand(0.25)}
      />
      <IconButton
        icon={<Maximize2 className="h-4 w-4" />}
        label="Fit to View (Ctrl+0)"
        onClick={sendFitToView}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-0.5 rounded px-1.5 py-1 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-100 transition-colors"
            title="Auto Layout (Ctrl+L)"
          >
            <Workflow className="h-4 w-4" />
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onSelect={() => dispatchAutoLayout(0)}>
            Sugiyama (Layered)
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => dispatchAutoLayout(0)}>
            DAG (Topological)
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => dispatchAutoLayout(1)}>
            Force-directed
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Divider */}
      <div className="mx-1 h-5 w-px bg-zinc-700" />

      {/* Section 4: Tool Selector */}
      <IconButton
        icon={<MousePointer2 className="h-4 w-4" />}
        label="Select (V)"
        active={currentTool === "select"}
        onClick={() => handleToolChange("select")}
      />
      <IconButton
        icon={<Hand className="h-4 w-4" />}
        label="Pan (H)"
        active={currentTool === "pan"}
        onClick={() => handleToolChange("pan")}
      />
      <IconButton
        icon={<Spline className="h-4 w-4" />}
        label="Connect (C)"
        active={currentTool === "addConnection"}
        onClick={() => handleToolChange("addConnection")}
      />

      {/* Divider */}
      <div className="mx-1 h-5 w-px bg-zinc-700" />

      {/* Layout toggle */}
      <IconButton
        icon={<LayoutDashboard className="h-4 w-4" />}
        label="Single View"
        active={layoutMode === "single"}
        onClick={() => setLayoutMode("single")}
        size="sm"
      />
      <IconButton
        icon={<Columns2 className="h-4 w-4" />}
        label="Split Horizontal"
        active={layoutMode === "split-horizontal"}
        onClick={() => cycleLayout("split-horizontal")}
        size="sm"
      />
      <IconButton
        icon={<Rows2 className="h-4 w-4" />}
        label="Split Vertical"
        active={layoutMode === "split-vertical"}
        onClick={() => cycleLayout("split-vertical")}
        size="sm"
      />

      {/* Spacer to push run controls to the right */}
      <div className="flex-1" />

      {/* Section 5: Run Controls */}
      <IconButton
        icon={isDeploying
          ? <Loader2 className="h-4 w-4 animate-spin" />
          : <Rocket className="h-4 w-4" />}
        label={isDeploying ? "Deploying..." : "Deploy"}
        disabled={!canDeploy}
        onClick={deploy}
      />
      <IconButton
        icon={<Play className="h-4 w-4" />}
        label="Run"
        disabled={!canRun}
        onClick={run}
      />
      <IconButton
        icon={isStopping
          ? <Loader2 className="h-4 w-4 animate-spin" />
          : <Square className="h-4 w-4" />}
        label={isStopping ? "Stopping..." : "Stop"}
        disabled={!canStop}
        onClick={stop}
      />

      {/* Divider */}
      <div className="mx-1 h-5 w-px bg-zinc-700" />

      {/* Section 6: Debug Controls (conditionally rendered for BPMN tabs) */}
      <DebugControls />
    </div>
  );
}
